import "../../../libs/split-type/split-type.js";
import { IPlayer, PlayerController } from "../../../ts/eventfive/display/PlayerController.js";
import { type BlockEditProps, type BlockSettings } from "../../_core/BlockCore";
import { type Animation } from "./AnimationSupport";

interface AnimationRegistration {
    key: string;
    title: string;
    filter?: (options: { props: BlockEditProps<any>; settings: BlockSettings<any> }) => boolean;
    create: (options: { targets: HTMLElement[]; timeline: gsap.core.Timeline; itemOptions: AnimationPlayerOptions }) => void;
    dispose?: (options: { targets: HTMLElement[]; timeline: gsap.core.Timeline; itemOptions: AnimationPlayerOptions }) => void;
}

interface TimelineItem {
    timeline: gsap.core.Timeline;
    dispose(): void;
}

declare global {
    interface WindowEventMap extends GlobalEventHandlersEventMap, WindowEventHandlersEventMap {
        "e5-register-animation": CustomEvent<{ view: AnimationView }>;
    }
}

export class AnimationView {
    public readonly registrations: { [key: string]: AnimationRegistration } = {};

    constructor() {
        console.log("[eventfive.wordpress-plugin]", "[AnimationView]");

        e5.Debug.add({
            uid: "e5-animation",
            tooltip: "Animation Debug",
            icon: "fal fa-magic",
            localStorage: true,
            onToggleInit: (debug) => {
                document.body.classList.toggle("e5-animation--debug", debug.active);
            }

        })

        this.register({
            key: "fade-in",
            title: "Fade In",
            create: ({ targets, timeline, itemOptions }) => {
                targets.forEach((item, index) => {
                    timeline.from(
                        item,
                        {
                            opacity: 0,
                            duration: itemOptions.duration,
                            clearProps: "opacity",
                        },
                        // {
                        //     opacity: 1
                        // },
                        index * itemOptions.stagging);
                });
            },
            dispose: ({ targets, timeline }) => {
                targets.forEach((item, index) => {
                    item.style.opacity = null;
                });
            }
        });

        this.register({
            key: "fade-out",
            title: "Fade Out",
            create: ({ targets, timeline, itemOptions }) => {
                targets.forEach((item, index) => {
                    timeline.fromTo(
                        item,
                        {
                            opacity: 1
                        },
                        {
                            opacity: 0,
                            duration: itemOptions.duration
                        },
                        index * itemOptions.stagging);
                });
            },
            dispose: ({ targets, timeline }) => {
                targets.forEach((item, index) => {
                    item.style.opacity = null;
                });
            }
        });

        this.register({
            key: "fade-up",
            title: "Fade Up",
            create: ({ targets, timeline, itemOptions }) => {
                targets.forEach((item, index) => {
                    timeline.from(
                        item,
                        {
                            opacity: 0.15,
                            duration: itemOptions.duration,
                            transform: "translate3d(0, 32px, 0)",
                            clearProps: "opacity,transform"
                        },
                        index * itemOptions.stagging);
                });
            }
        });

        this.register({
            key: "elastic-up",
            title: "Elastic Up",
            create: ({ targets, timeline, itemOptions }) => {
                targets.forEach((item, index) => {
                    timeline.from(
                        item,
                        {
                            opacity: 0.1,
                            duration: itemOptions.duration,
                            clearProps: "opacity"
                        },
                        index * itemOptions.stagging);

                    timeline.from(
                        item,
                        {
                            transform: "translate3d(0, 32px, 0)",
                            duration: itemOptions.duration,
                            ease: "back.out(4)",
                            clearProps: "opacity,transform"
                        },
                        index * itemOptions.stagging);
                });
            }
        });

        this.register({
            key: "slide-up",
            title: "Slide Up",
            create: ({ targets, timeline, itemOptions }) => {
                targets.forEach((item, index) => {
                    timeline.add(gsap.fromTo(item,
                        { transform: "scale(0.8) translate3d(0, 32px, 0)", filter: "blur(10px)", opacity: 0.1 },
                        { duration: 1, transform: "none", filter: "blur(0px)", clearProps: "transform,filter", opacity: 1 }),
                        index * itemOptions.stagging);
                });
            }
        });

        this.register({
            key: "scale-up",
            title: "Scale Up",
            create: ({ targets, timeline, itemOptions }) => {
                targets.forEach((item, index) => {
                    timeline.add(gsap.fromTo(item,
                        { transform: "scale(0.9)", opacity: 0 },
                        { duration: itemOptions.duration, transform: "none", opacity: 1, clearProps: "transform, opacity" }),
                        index * itemOptions.stagging);
                });
            }
        });

        this.register({
            key: "clip-path-inset",
            title: "Clip Path Inset",
            create: ({ targets, timeline, itemOptions }) => {
                targets.forEach((item, index) => {
                    timeline.add(gsap.fromTo(item,
                        // { clipPath: "inset(25% round 20px)", opacity: 0.3, },
                        // { clipPath: "inset(0px round 20px)", opacity: 1, clearProps: "inset, opacity" }),
                        { transform: "scale(0.8)", opacity: 0.3, },
                        { transform: "none", opacity: 1, clearProps: "transform, opacity" }),
                        index * itemOptions.stagging);
                });
            }
        });

        this.register({
            key: "text-lines",
            title: "Text Lines (Experimental)",
            filter: ({ props }) => {
                return props.name === "e5/text-box";
            },
            create: ({ targets, timeline }) => {
                targets.forEach((item, index) => {

                    const wrap = document.createElement("div");
                    while (item.firstChild) {
                        wrap.appendChild(item.firstChild);
                    }
                    item.appendChild(wrap);

                    const splits: SplitType[] = [];
                    const paragraphs = [...Array.from(item.querySelectorAll<HTMLElement>("*"))];
                    let charIndex = 0;
                    for (let i = 0; i < paragraphs.length; i++) {
                        const paragraph = paragraphs[i];
                        paragraph.style.hyphens = "none";

                        const split = new SplitType(paragraph, { types: "chars,words", absolute: false });

                        const onlySr = document.createElement("span");
                        onlySr.classList.add("sr-only");
                        onlySr.textContent = paragraph.textContent;
                        paragraph.prepend(onlySr);

                        paragraph.querySelectorAll<HTMLElement>("*:not(.sr-only)").forEach((el, index) => {
                            el.setAttribute("aria-hidden", "true");
                        });

                        splits.push(split);
                        split.chars.forEach((char, index) => {
                            char.setAttribute("aria-hidden", "true");
                            timeline.from(
                                char,
                                {
                                    opacity: 0.25,
                                    duration: 1,
                                    clearProps: "opacity",
                                    onComplete: () => {
                                        // if (index == split.lines.length - 1) {
                                        //     split.revert();
                                        // }
                                    }
                                },
                                charIndex * 0.2);

                            charIndex++;
                        });
                    }
                });
            }
        });

        window.addEventListener("resize", () => { });

        window.dispatchEvent(
            new CustomEvent("e5-register-animation", {
                bubbles: false,
                cancelable: false,
                detail: {
                    view: this
                }
            })
        );

        if (e5.Plugins.config.isAdmin) {
            return;
        }

        e5.component.register({
            selector: `[data-e5-animation]`,
            callback: (element: HTMLElement) => {

                const options: Animation = e5.object.merge<Animation>({
                    name: "fade-in",
                    once: true,
                    // duration: 1,
                    stagging: 0,
                    start: 0,
                    end: 1
                }, JSON.parse(element.getAttribute("data-e5-animation")));

                new AnimationPlayer(element, this, options);

                element.classList.add("e5-animation--ready");
            }
        });
    }

    public register(registration: AnimationRegistration) {
        this.registrations[registration.key] = registration;
    }

    public create(animation: string, targets: HTMLElement | HTMLElement[], options?: AnimationPlayerOptions): TimelineItem | null {
        let timeline = gsap.timeline();
        timeline.pause();

        if (this.registrations[animation]) {
            this.registrations[animation].create({
                targets: Array.isArray(targets) ? targets : [targets],
                timeline: timeline,
                itemOptions: options
            });
        } else {
            console.warn(`Animation "${animation}" not found.`);
            return null;
        }

        return {
            timeline,
            dispose: () => {
                timeline.kill();
                timeline = null;

                if (this.registrations[animation]
                    && typeof this.registrations[animation].dispose === "function") {
                    this.registrations[animation].dispose({
                        targets: Array.isArray(targets) ? targets : [targets],
                        timeline: timeline,
                        itemOptions: options
                    });
                }
            }
        };
    }
}

interface AnimationPlayerOptions {
    kind?: string;
    duration?: number;
    once?: boolean;
    targets?: string;
    playerController?: string;
    maxTargets?: number;
    stagging?: number;
}

class AnimationPlayer implements IPlayer<AnimationPlayer> {

    public isReady: boolean = true;
    public onReady = e5.Signal.create<[AnimationPlayer]>();
    public onEnterFrame = e5.Signal.create<[AnimationPlayer]>();

    private _timeline: TimelineItem;
    private _controller: PlayerController;
    private _element: HTMLElement;
    private _completed: number = 0;
    private _progress: number = 0;

    constructor(element: HTMLElement, view: AnimationView, options: Animation) {

        this._element = element;

        options = e5.object.merge<Animation>({
            name: "fade-in",
            once: true,
            stagging: 0,
            start: 0,
            end: 1,
            duration: "0.7s",
            timeline: null,
            scrollView: null
        }, options);

        let elements: HTMLElement[] = [element];
        if (options.stagging) {
            elements = Array.from(element.querySelectorAll(".e5-animation--stagging")) as HTMLElement[];
        }

        //TODO: default duration...
        let duration = 0.7;
        if (options.duration) {
            duration = parseFloat(options.duration);
            if (options.duration.endsWith("ms")) {
                duration = duration / 1000;
            }
        }

        const stagging = (options.stagging * duration) / elements.length;
        this._timeline = view.create(options.name || "fade-in", elements, { stagging: stagging, duration: duration });

        this._controller = new PlayerController({
            once: options.once,
            start: options.start,
            end: options.end,
            duration: duration,
            loop: false,//TODO: options.loop,
            scrollView: options.scrollView,
            timeline: options.timeline,
            player: this,
            element,
        });
    }

    public get progress(): number {
        return this._progress;
    }

    public set progress(value: number) {
        value = e5.Calc.clamp(value, 0, 1);
        if (this._progress === value) {
            return;
        }
        this._progress = value;

        this._element.style.setProperty("--e5-animation--progress", value.toString());

        if (this._timeline) {
            this._timeline.timeline.progress(value, false);
        }
        this.onEnterFrame.dispatch(this);

        if (value >= 1) {
            this._completed++;
            if (this._completed === 1) {
                //TODO: maybe dispose
            }
        }
    }

    public dispose(): void {
        this._controller.dispose();
        this._controller = null;
        if (this._timeline) {
            this._timeline.dispose();
        }
        this._timeline = null;
    }
}